
import { Component, Vue, Watch } from 'vue-property-decorator';
import { debounce } from 'lodash';

import { sharedState } from '../../../state';
import { deleteUser } from '../../../client/users';
import { UserIdentity } from '../../../../../common/framework/model/UserIdentity';
import { Group } from '../../../../../common/framework/model/Group';
import { FrameworkResource } from '../../../../../common/framework/enumeration/FrameworkResource';
import { SortOrder } from '../../../../../common/framework/model/SortOrder';
import { QueryParameters } from '../../../../../common/application/model/QueryParameters';

import TextComponent from '../../../fields/TextComponent.vue';
import SelectComponent from '../../../fields/SelectComponent.vue';
import { OptionValue, getResourceOptions } from '../../../service/options';

import { addDelayedWindowResizeListenerFunction } from '../../../service/window_resize_service';
import { getResourcePaging, getResources } from '../../../client/resource';
import { UserGroup } from '../../../../../common/framework/model/UserGroup';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';

@Component({
    components: { TextComponent, SelectComponent },
})
export default class Users extends Vue {
    shared = sharedState;
    local = {
        rows: [] as UserIdentity[],
        total: 0,
        loading: false,
        parameters: {
            pageSize: 100,
            sortField: 'lastName',
            sortOrder: SortOrder.ASC,
            page: 1,
        } as QueryParameters,
        selected: undefined as UserIdentity | undefined,
        showLocked: false,
        selectedUserEmailPrefix: undefined as string | undefined,
        selectedUserFirstNamePrefix: undefined as string | undefined,
        selectedUserLastNamePrefix: undefined as string | undefined,
        selectedUserGroupPrefix: undefined as string | undefined,
        userGroups: [] as OptionValue[],
    };

    @Watch('local.showLocked')
    async showLockedChanged() {
        await this.loadAsyncData();
    }

    async mounted() {
        addDelayedWindowResizeListenerFunction(this, this.loadAsyncData);
        await this.loadAsyncData();
    }

    async onPageChange(page: number) {
        this.local.parameters.page = page;
        await this.loadAsyncData();
    }

    add() {
        this.$router.push('/users/add-user');
    }

    edit() {
        if (this.local.selected) {
            this.$router.push('/users/edit-user/' + this.local.selected.id);
        }
    }

    changePassword() {
        if (this.local.selected) {
            this.$router.push('/users/change-user-password/' + this.local.selected.id);
        }
    }

    confirmDelete() {
        this.$buefy.dialog.confirm({
            title: this.$t('title.confirmDeleteUser').toString(),
            message: this.$t('message.confirmDeleteUser').toString(),
            cancelText: this.$t('button.cancel').toString(),
            confirmText: this.$t('button.ok').toString(),
            type: 'is-success',
            onConfirm: async () => {
                if (this.local.selected) {
                    await deleteUser(this.local.selected.id);
                    await this.loadAsyncData();
                }
            },
        });
    }

    @Watch('local.selectedUserEmailPrefix')
    @Watch('local.selectedUserFirstNamePrefix')
    @Watch('local.selectedUserLastNamePrefix')
    @Watch('local.selectedUserGroupPrefix')
    async selectedAssetNameChange() {
        await this.loadAsyncData();
    }

    async loadAsyncData() {
        const backendParameters = new Map<string, any>(Object.entries(this.local.parameters));

        await this.debouncedLoad(backendParameters);
    }

    debouncedLoad = debounce(async (parameters: Map<string, any>) => {
        if (this.local.loading) {
            return;
        }

        this.local.loading = true;

        const queryParts: Array<string> = [];
        parameters.delete('query');
        parameters.delete('email');
        parameters.delete('firstName');
        parameters.delete('lastName');

        if (this.local.selectedUserEmailPrefix) {
            queryParts.push('email ILIKE {email}');
            parameters.set('email', '%' + this.local.selectedUserEmailPrefix + '%');
        }
        if (this.local.selectedUserFirstNamePrefix) {
            queryParts.push('firstName ILIKE {firstName}');
            parameters.set('firstName', '%' + this.local.selectedUserFirstNamePrefix + '%');
        }
        if (this.local.selectedUserLastNamePrefix) {
            queryParts.push('lastName ILIKE {lastName}');
            parameters.set('lastName', '%' + this.local.selectedUserLastNamePrefix + '%');
        }

        if (queryParts.length > 0) {
            parameters.set('query', queryParts.join(' AND '));
        }

        parameters.set('page', this.local.parameters.page - 1);

        this.local.total = (await getResourcePaging(FrameworkResource.USER, parameters)).rowCount;

        parameters.delete('page');
        this.local.rows = await getResources<UserIdentity>(
            FrameworkResource.USER,
            this.local.parameters.page - 1,
            parameters,
        );

        this.local.userGroups = await getResourceOptions(FrameworkResource.GROUP, 'name');

        if (this.local.selectedUserGroupPrefix) {
            await this.filterByUserGroup(this.local.selectedUserGroupPrefix);
        }

        this.local.loading = false;
        if (this.local && this.local.rows.indexOf(this.local.selected!!) == -1) {
            this.local.selected = undefined;
        }
    }, 300);

    async filterByUserGroup(groupId: string) {
        const userGroupParameters: Map<string, any> = new Map<string, any>();
        userGroupParameters.set('groupId', groupId);

        const userGroups = await getResources<UserGroup>(FrameworkResource.USER_GROUP, -1, userGroupParameters);

        const userGroupsUserIds = userGroups.map((group) => {
            return group.userId;
        });

        this.local.rows = this.local.rows.filter((row) => userGroupsUserIds.some((id) => id === row.id));
    }

    onSortChange(sortField: string, sortOrder: SortOrder) {
        this.local.parameters = {
            ...this.local.parameters,
            sortField,
            sortOrder,
            page: 1,
        };

        this.loadAsyncData();
    }

    onRowClick(row: UserIdentity): void {
        if (this.local.selected && row.id === this.local.selected.id) {
            this.local.selected = undefined;
        }
    }
}
